import { type FC, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Typography, Button, Space } from 'antd';
import {
  ArrowRightOutlined,
  CalendarOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { ResumeModal, downloadResume } from '@/components/ResumeModal';
import { useSiteContent } from '@/context/ContentProvider';
import { trackCTAClick, trackCalendlyClick, trackResumeDownload } from '@/utils/analytics';

const { Title, Paragraph, Text } = Typography;

/**
 * Rounds a count down to the nearest "nice" step.
 * e.g. 69 → "60+", 142 → "140+", 1384 → "1300+", 15280 → "15000+"
 */
const formatCount = (n: number): string => {
  if (n < 10) return `${n}`;
  const magnitude = Math.pow(10, Math.floor(Math.log10(n)));
  const step = magnitude >= 100 ? magnitude / 10 : 10;
  const rounded = Math.floor(n / step) * step;
  return `${rounded.toLocaleString()}+`;
};

/**
 * Animated text that smoothly transitions between values with a slide-up + blur effect.
 */
const AnimatedValue: FC<{ value: string }> = ({ value }) => {
  const [display, setDisplay] = useState(value);
  const [animating, setAnimating] = useState(false);
  const prevValue = useRef(value);

  useEffect(() => {
    if (prevValue.current === value) return;
    prevValue.current = value;
    setAnimating(true);
    const t = setTimeout(() => {
      setDisplay(value);
      const t2 = setTimeout(() => setAnimating(false), 50);
      return () => clearTimeout(t2);
    }, 200);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <span
      style={{
        display: 'inline-block',
        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        transform: animating ? 'translateY(-6px)' : 'translateY(0)',
        opacity: animating ? 0 : 1,
        filter: animating ? 'blur(4px)' : 'blur(0px)',
      }}
    >
      {display}
    </span>
  );
};

/** Module-level cache so the count survives route changes without refetching. */
let cachedDownloadCount: number | null = null;

/** Module-level flag — once the trust signals have fade-in'd in this session,
 *  skip the animation on subsequent Home remounts (e.g. returning from a
 *  project detail page) so the words don't visibly reappear. */
let trustHasAnimated = false;

// Media query that mirrors the CSS mobile breakpoint. Used to *skip rendering*
// the background <video> on small screens so the ~50MB asset isn't fetched.
const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

export const Hero: FC = () => {
  const [resumeOpen, setResumeOpen] = useState(false);
  const [downloadCount, setDownloadCount] = useState<number | null>(cachedDownloadCount);
  const [videoReady, setVideoReady] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA_QUERY).matches,
  );
  const { hero } = useSiteContent();
  const videoUrl = hero.backgroundVideo;
  const showVideo = !!videoUrl && !isMobile;

  useEffect(() => {
    if (cachedDownloadCount !== null) return;
    fetch('/api/resume?action=download-count')
      .then((r) => r.json())
      .then((d) => {
        if (d.count > 0) {
          cachedDownloadCount = d.count;
          setDownloadCount(d.count);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setVideoReady(false);
  }, [videoUrl]);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Capture once per mount: did the trust signals run their fade-in before?
  // Stable across re-renders so swapping themes / state changes don't drop
  // the static state mid-render.
  const trustStaticRef = useRef(trustHasAnimated);
  useEffect(() => {
    // After this Hero mounted, future mounts should see the static class.
    trustHasAnimated = true;
  }, []);

  const handleCalendlyClick = () => {
    trackCalendlyClick('hero');
    window.open(hero.ctaLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <section
      className={`section hero-section${showVideo ? ' hero-section--has-video' : ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        // Full viewport — floating navbar overlays the top of the hero,
        // and its backdrop-blur picks up the hero (video / image) behind it.
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      {showVideo && (
        <div className="hero-video" aria-hidden="true">
          <video
            key={videoUrl}
            className={`hero-video__media${videoReady ? ' is-ready' : ''}`}
            src={videoUrl}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onCanPlay={() => setVideoReady(true)}
          />
          <div className="hero-video__overlay" />
        </div>
      )}

      <div className="container fade-in-up" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: 680 }}>
          <div style={{ marginBottom: 32 }}>
            <div className="status-available" style={{ marginBottom: 12 }}>
              <span className="status-dot" />
              {hero.statusBadge}
            </div>
            {(hero.availabilityTimeline ||
              (hero.preferredRoles && hero.preferredRoles.length > 0)) && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                {hero.availabilityTimeline && (
                  <Text
                    style={{
                      fontSize: 13,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      background: 'rgba(176, 150, 125, 0.08)',
                      padding: '4px 12px',
                      borderRadius: 16,
                      border: '1px solid rgba(176, 150, 125, 0.15)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    <ClockCircleOutlined style={{ fontSize: 12 }} />
                    {hero.availabilityTimeline}
                  </Text>
                )}
                {hero.preferredRoles?.map((role) => (
                  <Text
                    key={role}
                    style={{
                      fontSize: 13,
                      background: 'rgba(176, 150, 125, 0.08)',
                      padding: '4px 12px',
                      borderRadius: 16,
                      border: '1px solid rgba(176, 150, 125, 0.15)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {role}
                  </Text>
                ))}
              </div>
            )}
          </div>

          <Title
            level={1}
            style={{
              fontSize: 'clamp(32px, 8vw, 56px)',
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: '-0.03em',
              marginBottom: 24,
              wordBreak: 'break-word',
            }}
          >
            {hero.title}{' '}
            <span style={{ color: 'var(--color-accent)' }}>
              {hero.titleAccent}
            </span>
          </Title>

          <Paragraph
            style={{
              fontSize: 19,
              lineHeight: 1.7,
              opacity: 0.7,
              marginBottom: 40,
              maxWidth: 540,
            }}
          >
            {hero.subtitle}
          </Paragraph>

          <Space wrap>
            <Button
              type="primary"
              size="large"
              icon={<CalendarOutlined />}
              onClick={handleCalendlyClick}
              style={{ height: 48, paddingInline: 28, fontWeight: 600 }}
            >
              {hero.ctaText}
            </Button>
            <Link to={hero.secondaryCtaLink}>
              <Button
                size="large"
                icon={<ArrowRightOutlined />}
                onClick={() => trackCTAClick('view_work', 'hero')}
                style={{ height: 48, paddingInline: 28 }}
              >
                {hero.secondaryCtaText}
              </Button>
            </Link>
            <Button
              type="text"
              size="large"
              icon={<DownloadOutlined />}
              onClick={() => {
                downloadResume();
                trackResumeDownload();
                setDownloadCount((c) => {
                  const next = (c ?? 0) + 1;
                  cachedDownloadCount = next;
                  return next;
                });
                setResumeOpen(true);
              }}
              style={{ height: 48, paddingInline: 20, opacity: 0.7 }}
            >
              {hero.resumeButtonText}
              <span
                style={{
                  fontSize: 12,
                  opacity: 0.5,
                  marginLeft: 4,
                  overflow: 'hidden',
                }}
              >
                (
                <AnimatedValue
                  value={
                    downloadCount === null ? '--' : formatCount(downloadCount)
                  }
                />
                )
              </span>
            </Button>
          </Space>

          <ResumeModal open={resumeOpen} onClose={() => setResumeOpen(false)} />

          {/* Trust signals */}
          <div style={{ marginTop: 64, opacity: 0.5 }}>
            <Paragraph style={{ fontSize: 13, marginBottom: 8 }}>
              {hero.trustLabel.split(' ').map((word, i) => (
                <span
                  key={i}
                  className={`trust-shimmer-word${trustStaticRef.current ? ' is-static' : ''}`}
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  {word}
                </span>
              ))}
            </Paragraph>
            <Space size={32} wrap>
              {hero.trustCompanies.map((name, i) => {
                const labelWordCount = hero.trustLabel.split(' ').length;
                const baseDelay = labelWordCount * 0.08 + 0.3;
                return (
                  <span
                    key={name}
                    className={`trust-shimmer-company${trustStaticRef.current ? ' is-static' : ''}`}
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      animationDelay: `${baseDelay + i * 0.2}s`,
                    }}
                  >
                    {name}
                  </span>
                );
              })}
            </Space>
          </div>
        </div>
      </div>
    </section>
  );
};
